import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CheckCircle, Clock, AlertCircle, User } from 'lucide-react';

interface Task {
  id: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in-progress' | 'completed' | 'failed';
  estimated_time: number;
  assigned_agent_id?: string;
}

interface Agent {
  id: string;
  name: string;
}

interface TaskBoardProps {
  tasks: Task[];
  agents: Agent[];
  onTaskClick?: (task: Task) => void;
  onAssignAgent?: (taskId: string, agentId: string) => void;
}

export function TaskBoard({ tasks, agents, onTaskClick, onAssignAgent }: TaskBoardProps) {
  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'low': return 'bg-blue-500/20 text-blue-700';
      case 'medium': return 'bg-green-500/20 text-green-700';
      case 'high': return 'bg-orange-500/20 text-orange-700';
      case 'urgent': return 'bg-red-500/20 text-red-700';
      default: return 'bg-gray-500/20 text-gray-700';
    }
  };

  const getStatusBadge = (status: Task['status']) => {
    switch (status) {
      case 'pending': return <Badge className="bg-yellow-500/20 text-yellow-700">Pending</Badge>;
      case 'in-progress': return <Badge className="bg-blue-500/20 text-blue-700">In Progress</Badge>;
      case 'completed': return <Badge className="bg-green-500/20 text-green-700">Completed</Badge>;
      case 'failed': return <Badge className="bg-red-500/20 text-red-700">Failed</Badge>;
      default: return <Badge>Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tasks.map((task) => (
          <Card
            key={task.id}
            className="hover:shadow-lg transition-shadow duration-200 cursor-pointer"
            onClick={() => onTaskClick?.(task)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{task.title}</CardTitle>
                  <CardDescription>{task.description}</CardDescription>
                </div>
                <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" /> {task.estimated_time} min
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(task.status)}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  {task.assigned_agent_id ? (
                    <span>Assigned</span>
                  ) : (
                    <span>Unassigned</span>
                  )}
                </div>
                {!task.assigned_agent_id && agents[0] && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssignAgent?.(task.id, agents[0].id);
                    }}
                  >
                    Assign First Agent
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {tasks.length === 0 && (
        <div className="text-center py-12">
          <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">No tasks available</p>
        </div>
      )}
    </div>
  );
}
